import { type ReactNode, createContext, useContext, useMemo } from 'react';

type DocumentNavigationContextValue = {
  hasDocumentRoute: boolean;
  documentOrigin?: string;
  documentPathPatterns: readonly string[];
  buildDocumentHref: (documentId: string) => string;
  openDocument?: (documentId: string) => void;
};

const defaultValue: DocumentNavigationContextValue = {
  hasDocumentRoute: false,
  documentPathPatterns: [],
  buildDocumentHref: (documentId: string) =>
    `/collaborativeDocuments/${documentId}`,
};

const DocumentNavigationContext =
  createContext<DocumentNavigationContextValue>(defaultValue);

export const DocumentNavigationProvider = ({
  children,
  buildDocumentHref,
  openDocument,
  documentOrigin,
  documentPathPatterns,
}: {
  children: ReactNode;
  buildDocumentHref?: (documentId: string) => string;
  openDocument?: (documentId: string) => void;
  documentOrigin?: string;
  documentPathPatterns?: readonly string[];
}) => {
  const parent = useContext(DocumentNavigationContext);
  const value = useMemo(
    () => ({
      buildDocumentHref: buildDocumentHref ?? parent.buildDocumentHref,
      openDocument: openDocument ?? parent.openDocument,
      documentOrigin: documentOrigin ?? parent.documentOrigin,
      documentPathPatterns: documentPathPatterns ?? parent.documentPathPatterns,
      hasDocumentRoute: !!buildDocumentHref || parent.hasDocumentRoute,
    }),
    [
      buildDocumentHref,
      openDocument,
      documentOrigin,
      documentPathPatterns,
      parent,
    ],
  );

  return (
    <DocumentNavigationContext.Provider value={value}>
      {children}
    </DocumentNavigationContext.Provider>
  );
};

export const useDocumentNavigation = (): DocumentNavigationContextValue =>
  useContext(DocumentNavigationContext);
